/**
 * Minimal placeholder primitives for the app shell and the phase-3 placeholder screens.
 * Phase 4 replaces their usages with the comic UI kit (`@/ui`).
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'danger';

const variants: Record<Variant, string> = {
  primary: 'bg-yellow',
  secondary: 'bg-white',
  danger: 'bg-team-b text-white',
};

export function Btn({
  variant = 'secondary',
  className = '',
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      type={type}
      className={`rounded-md border-[3px] border-ink px-3 py-1.5 font-extrabold shadow-ink-sm active:translate-x-0.5 active:translate-y-0.5 active:shadow-ink-xs disabled:opacity-50 ${variants[variant]} ${className}`}
      {...props}
    />
  );
}

export function Card({
  title,
  children,
  className = '',
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border-[3px] border-ink bg-white p-4 shadow-ink ${className}`}>
      {title ? <h2 className="comic mb-3 text-2xl">{title}</h2> : null}
      {children}
    </section>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <main className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-4">{children}</main>;
}

export function CenterCard({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <Page>
      <div className="mx-auto mt-10 w-full max-w-md">
        <Card title={title}>{children}</Card>
      </div>
    </Page>
  );
}

export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-extrabold">
      <span>{label}</span>
      {children}
    </label>
  );
}

export const inputClass =
  'rounded-md border-[3px] border-ink bg-white px-3 py-2 text-base font-bold shadow-ink-xs outline-none focus:bg-yellow-soft';
